import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import CloseIcon from '@mui/icons-material/Close';

import {
  CoreDocumentType,
  DocumentsSections,
} from '@cd3p/core/modules/documents';
import {
  APIDocument,
  APIDocumentSearchResult,
  APIUserType,
} from '@cd3p/core/types/api';
import { mapLanguageToApiLanguage } from '@cd3p/core/utils/docs';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { IconButton, Stack, SvgIcon, Typography } from 'components';
import { usePrevious } from 'react-use';

import { AllHelpArticlesList } from './AllHelpArticles';
import { CommonHelpTopicsList } from './CommonHelpTopicsList';
import { HelpCenterFooter } from './HelpCenterFooter';
import { SearchResults } from './SearchResults';

import { useSelector, useTranslation } from 'third-party';

import {
  DEFAULT_HELP_CENTER_SEARCH_COLUMNS,
  useHelpCenter,
} from 'modules/helpCenter';
import { useStorage } from 'modules/storage';

import { appSelectors, helpCenterSelectors } from 'selectors';

import {
  LEFT_OVERLAY_PANEL_WIDTH,
  LeftOverlayPanelContainer,
} from 'components/LeftOverlayPanelContainer/LeftOverlayPanelContainer';

import {
  docsComponents,
  filterContentByUserType,
} from 'features/Docs/Components';

import { OutlinedSearchInput } from 'styles/common';

const InnerContent = styled(Stack)`
  flex-grow: 1;
  min-width: ${LEFT_OVERLAY_PANEL_WIDTH};
  max-width: ${LEFT_OVERLAY_PANEL_WIDTH};
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
`;

const Header = styled(Stack)`
  min-height: 4.8rem;
  height: 4.8rem;
  align-items: center;
  padding: 0 1rem 0 1rem;
  border-bottom: 1px solid ${props => props.theme.custom.palette.muted100};
`;

const Title = styled(Typography)`
  color: ${props => props.theme.custom.palette.primary900};
  margin-left: 0.5rem;
`;

const SearchPanel = styled(Stack)`
  border-bottom: 1px solid ${props => props.theme.custom.palette.muted50};
  background: ${props => props.theme.custom.palette.backgroundTheme};
  justify-content: flex-end;
  padding: 0.8rem;
`;

const StyledOutlinedSearchInput = styled(OutlinedSearchInput)`
  width: 100%;
`;

const CloseButton = styled(IconButton)`
  margin-left: auto;
`;

const Content = styled(Stack)`
  padding: 1.2rem 1.6rem;
  overflow-y: scroll;
  flex: 1;
`;

const getNextArticleFromFlatTreeArray = (
  currentArticle: APIDocument | null,
  articlesArray: CoreDocumentType[],
): APIDocument | null => {
  if (!currentArticle) {
    return null;
  }
  let currentArticleIndex: number | null = null;
  let firstArticle: APIDocument | null = null;
  let nextArticle: APIDocument | null = null;
  articlesArray.every((article, index) => {
    if (article.id === currentArticle?.id) {
      currentArticleIndex = index;
    }
    if (!firstArticle && !article.document.isCategory) {
      firstArticle = article.document;
    }
    if (
      currentArticleIndex !== null &&
      currentArticleIndex < index &&
      !article.document.isCategory
    ) {
      nextArticle = article.document;
      return false;
    }
    return true;
  });
  // if nextArticle doesn't exist, return first article from the list
  return nextArticle || firstArticle;
};

const getFirstArticleFromSelectedCategory = (
  categoryTitle: string,
  articlesArray: CoreDocumentType[],
): APIDocument | null => {
  if (!categoryTitle) {
    return null;
  }
  let categoryDocument: CoreDocumentType | null = null;
  let firstArticle: APIDocument | null = null;
  const nestedCategoriesIds: Array<string | null> = [];
  articlesArray.every(article => {
    if (article.name === categoryTitle) {
      categoryDocument = article;
    }
    if (
      categoryDocument !== null &&
      (categoryDocument.id === article.document.parentId ||
        nestedCategoriesIds.includes(article.document.parentId))
    ) {
      if (!article.document.isCategory) {
        firstArticle = article.document;
        return false;
      } else {
        nestedCategoriesIds.push(article.document.id);
      }
    }
    return true;
  });
  return firstArticle as APIDocument | null;
};

export const DIALOG_ITEM_HEIGHT = 85;
export const CONTRACTOR_DIALOG_ITEM_HEIGHT = 65;

export const HelpCenterPanelContent = () => {
  const { t } = useTranslation();
  const [activeArticle, setActiveArticle] = useState<APIDocument | null>(null);
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  const [isArticleSearchActive, setIsArticleSearchActive] =
    useState<boolean>(false);

  const { updateUserSettingInStorage } = useStorage();

  const userType = useSelector(appSelectors.userType);
  const userLanguage = useSelector(appSelectors.userLanguage);

  const sectionId =
    userType === APIUserType.Contractor
      ? DocumentsSections.HelpCenterWebContractor
      : DocumentsSections.HelpCenterWebDispatcher;

  const { loadHelpCenterDocuments } = useHelpCenter(
    sectionId,
    mapLanguageToApiLanguage(userLanguage),
  );
  const helpCenterSortedItems = useSelector(
    helpCenterSelectors.helpCenterSortedItems,
  );
  const helpCenterDocuments = useSelector(helpCenterSelectors.helpCenterItems);
  const helpCenterItemsLoading = useSelector(
    helpCenterSelectors.helpCenterItemsLoading,
  );

  const providerId = useSelector(appSelectors.providerId);

  const nextArticle = useMemo(
    () => getNextArticleFromFlatTreeArray(activeArticle, helpCenterSortedItems),
    [activeArticle, helpCenterSortedItems],
  );

  const onSearchChange = useCallback(
    (value: string | null) => {
      setSearchKeyword(value);
      if (!activeArticle) {
        if (value) {
          loadHelpCenterDocuments({
            fullTextSearch: {
              pattern: value,
              columns: DEFAULT_HELP_CENTER_SEARCH_COLUMNS,
            },
          });
        } else {
          loadHelpCenterDocuments();
        }
      }
    },
    [activeArticle, loadHelpCenterDocuments],
  );

  useEffect(() => {
    // todo: decide in which place better fetch categories list
    loadHelpCenterDocuments();
  }, [loadHelpCenterDocuments]);

  const handleArticleSelect = useCallback((article: APIDocument) => {
    if (!article.isCategory) {
      setActiveArticle(article);
      setIsArticleSearchActive(true);
    }
  }, []);

  const handleNextArticleClick = useCallback(() => {
    if (nextArticle) {
      setActiveArticle(nextArticle);
    }
  }, [nextArticle]);

  const handleCommonCategorySelect = useCallback(
    (categoryTitle: string) => {
      const firstArticle = getFirstArticleFromSelectedCategory(
        categoryTitle,
        helpCenterSortedItems,
      );
      if (firstArticle) {
        setActiveArticle(firstArticle);
      }
    },
    [helpCenterSortedItems],
  );

  const onBackClick = useCallback(() => {
    setActiveArticle(null);
    if (isArticleSearchActive) {
      setSearchKeyword('');
      setIsArticleSearchActive(false);
    }
  }, [isArticleSearchActive]);

  const handleSearchResultClick = useCallback(
    (searchResultItem: APIDocumentSearchResult) => {
      if (!searchResultItem.document.isCategory) {
        setActiveArticle(searchResultItem.document);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (searchKeyword) {
        setSearchKeyword('');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderPanelContent = useCallback(() => {
    return activeArticle ? (
      <Content>
        <MarkdownPreview
          source={filterContentByUserType(
            activeArticle?.text,
            userType,
            searchKeyword,
          )}
          components={docsComponents as any}
          disableCopy
        />
      </Content>
    ) : (
      <>
        <CommonHelpTopicsList onSelectCategory={handleCommonCategorySelect} />
        <AllHelpArticlesList onSelect={handleArticleSelect} />
      </>
    );
  }, [
    activeArticle,
    handleArticleSelect,
    handleCommonCategorySelect,
    searchKeyword,
    userType,
  ]);

  return (
    <InnerContent>
      <Header direction="row">
        <Stack direction={'row'} alignItems={'center'}>
          {activeArticle ? (
            <IconButton onClick={onBackClick}>
              <SvgIcon height="1.2rem" width="0.74rem" icon="shevron-left" />
            </IconButton>
          ) : null}
          <Title variant="h5">{t('helpCenter.panel.title')}</Title>
        </Stack>
        <CloseButton
          onClick={() => updateUserSettingInStorage({ openedLeftPanel: null })}
        >
          <CloseIcon />
        </CloseButton>
      </Header>
      {!activeArticle || !searchKeyword || isArticleSearchActive ? (
        <SearchPanel direction="row">
          <StyledOutlinedSearchInput
            // reset search when changing provider
            key={providerId + activeArticle?.id}
            isClearable
            defaultValue={searchKeyword}
            onChange={onSearchChange}
            placeholder={
              isArticleSearchActive
                ? t('helpCenter.panel.search')
                : t('helpCenter.panel.helpSearch')
            }
          />
        </SearchPanel>
      ) : null}
      {searchKeyword && !activeArticle ? (
        <SearchResults
          searchData={helpCenterDocuments}
          searchLoading={helpCenterItemsLoading}
          onSelectItem={handleSearchResultClick}
          searchKeyword={searchKeyword}
        />
      ) : (
        renderPanelContent()
      )}
      <HelpCenterFooter
        showNextArticleLink={!!activeArticle}
        onNextArticleClick={handleNextArticleClick}
        nextArticleTitle={nextArticle?.title || ''}
      />
    </InnerContent>
  );
};

export const HelpCenterPanel = () => {
  const isHelpCenterPanelOpen = useSelector(appSelectors.isHelpCenterPanelOpen);
  const appOpenedLeftPanel = useSelector(appSelectors.appOpenedLeftPanel);
  const appLeftPanelAnimationDuration = useSelector(
    appSelectors.appLeftPanelAnimationDuration,
  );
  const previousAppOpenedLeftPanel = usePrevious(appOpenedLeftPanel);

  return (
    <LeftOverlayPanelContainer
      isPanelOpen={isHelpCenterPanelOpen}
      animationDuration={
        !!previousAppOpenedLeftPanel && !!appOpenedLeftPanel
          ? 0
          : appLeftPanelAnimationDuration
      }
    >
      <HelpCenterPanelContent />
    </LeftOverlayPanelContainer>
  );
};
